import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import PAYMENT_ORIGIN_POS from 'constants/payment/paymentOriginPos.const';
import fromAnyToCoinNum from 'utils/converters/any/fromAnyToCoinNum.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';

/**
 * Build payment method options dynamically based on POS flow
 * @param {boolean} posFlow - Whether POS flow is enabled
 * @returns {Object} Payment method options
 */
export const buildPaymentMethodOptions = (posFlow, t) => {
  const cardSource = posFlow === true ? DEPOSIT_SOURCE.pos : DEPOSIT_SOURCE.card;

  return {
    cash: {
      label: t('scheduler.clientOverview.addCredit.cash'),
      icon: 'cashBlack',
      value: DEPOSIT_SOURCE.cash,
    },
    [cardSource]: {
      label: t('scheduler.clientOverview.addCredit.card'),
      icon: 'creditCardBlack',
      value: cardSource,
    },
    other: {
      label: t('scheduler.clientOverview.addCredit.other'),
      icon: 'eGiftVoucherBlack',
      value: DEPOSIT_SOURCE.other,
    },
  };
};

/**
 * Validate credit amount and payment method
 * @param {string} creditAmount - The credit amount to validate
 * @param {string} paymentMethod - The payment method to validate
 * @param {Function} t - Translation function
 * @returns {string|null} Error message or null if valid
 */
export const validateAddCreditForm = (creditAmount, paymentMethod, t) => {
  if (!fromAnyToBool(paymentMethod)) {
    return t('scheduler.clientOverview.addCredit.errors.paymentMethod.required');
  }

  if (!fromAnyToBool(creditAmount)) {
    return t('scheduler.clientOverview.addCredit.errors.creditAmount.required');
  }

  const amountNum = parseFloat(creditAmount);
  if (Number.isNaN(amountNum) || amountNum <= 0) {
    return t('scheduler.clientOverview.addCredit.errors.creditAmount.min');
  }

  return null;
};

/**
 * Prepare deposit data for API submission
 * @param {Object} params - Parameters for deposit
 * @param {string} params.creditAmount - The credit amount
 * @param {string} params.paymentMethod - The payment method
 * @param {string} params.clientId - The client ID
 * @param {string} [params.paymentIntentId] - Optional payment intent ID for POS transactions
 * @returns {Object} Deposit data ready for API submission
 */
export const prepareDepositData = ({ creditAmount, paymentMethod, clientId, paymentIntentId = null }) => {
  const depositData = {
    amount: fromAnyToCoinNum(creditAmount),
    depositType: 'deposited',
    notes: '',
    clientId,
    used_for_appointmentIds: [],
    source: paymentMethod,
  };

  // Add payment intent ID for POS transactions
  if (paymentIntentId) {
    depositData.intent_id = paymentIntentId;
  }

  return depositData;
};

/**
 * Prepare payment items for POS transaction
 * @param {string} creditAmount - The credit amount
 * @param {Function} t - Translation function
 * @returns {Array} Payment items array
 */
export const preparePosPaymentItems = (creditAmount, t) => [
  {
    description: t('scheduler.clientOverview.addCredit.paymentDescription'),
    amount: fromAnyToCoinNum(creditAmount),
    price: fromAnyToCoinNum(creditAmount),
    quantity: 1,
  },
];

/**
 * Prepare POS reader options
 * @param {Object} params - Parameters for POS payment
 * @param {string} params.readerId - The reader ID
 * @param {Object} params.pos - POS state
 * @param {string} params.clientId - Client ID
 * @param {string} params.salonId - Salon ID
 * @param {string} params.creditAmount - Credit amount
 * @param {Function} params.t - Translation function
 * @returns {Object} Options for POS reader payment
 */
export const preparePosReaderOptions = ({ readerId, pos, clientId, salonId, creditAmount, t }) => ({
  readerId,
  pos,
  clientId,
  salonId,
  items: preparePosPaymentItems(creditAmount, t),
  paymentOrigin: PAYMENT_ORIGIN_POS.credit,
  amount: creditAmount,
});

/**
 * Determine if payment method requires card processing
 * @param {string} paymentMethod - The payment method
 * @param {boolean} posFlow - Whether POS flow is enabled
 * @returns {boolean} True if payment requires card processing
 */
export const isCardPaymentMethod = (paymentMethod, posFlow) => {
  const cardSource = posFlow === true ? DEPOSIT_SOURCE.pos : DEPOSIT_SOURCE.card;
  return paymentMethod === cardSource;
};
